import { useEffect, useMemo, useRef, useState } from "react";
import { parseBannerSlides } from "../helpers/bannerSlides";

const normalizeUrl = (url = "") => {
  const t = String(url || "").trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
};

const BannerSlider = ({ value = "", linkUrl = "" }) => {
  const slides = useMemo(
    () => parseBannerSlides(value, linkUrl),
    [value, linkUrl]
  );
  const [index, setIndex] = useState(0);
  const [animated, setAnimated] = useState(true);

  // Keep a ref to the interval so we can stop/restart it from any effect
  const timerRef = useRef(null);
  // Always-fresh ref to slides.length — safe to read inside effects/callbacks
  const slidesLenRef = useRef(slides.length);
  slidesLenRef.current = slides.length;

  // --- helpers (stable refs, never stale) ---
  const stopTimer = useRef(() => {
    clearInterval(timerRef.current);
    timerRef.current = null;
  });

  const startTimer = useRef(() => {
    stopTimer.current();
    if (slidesLenRef.current <= 1) return;
    timerRef.current = setInterval(() => {
      setAnimated(true);
      setIndex((i) => i + 1);
    }, 4000);
  });

  // Silent snap back to slide 0, then restart the timer
  const resetSlider = useRef(() => {
    stopTimer.current();
    setAnimated(false);
    setIndex(0);
    // Timer is restarted inside the `!animated` effect after rAF completes
  });

  // --- effects ---

  // Start timer on mount and whenever slide count changes
  useEffect(() => {
    startTimer.current();
    return () => stopTimer.current();
  }, [slides.length]);

  // Reset when source value changes (new setting saved)
  useEffect(() => {
    resetSlider.current();
  }, [value]);

  // Re-enable animation (and restart timer) after a silent position jump.
  // Two rAF frames ensure the browser commits the new transform first.
  useEffect(() => {
    if (!animated) {
      let raf1 = requestAnimationFrame(() => {
        let raf2 = requestAnimationFrame(() => {
          setAnimated(true);
          startTimer.current();
        });
        return () => cancelAnimationFrame(raf2);
      });
      return () => cancelAnimationFrame(raf1);
    }
  }, [animated]);

  // Snap-back after the clone slide via setTimeout — unlike onTransitionEnd,
  // setTimeout fires even when the tab is hidden, preventing index drift.
  useEffect(() => {
    if (index !== slides.length || slides.length === 0) return;
    const t = setTimeout(() => {
      setAnimated(false);
      setIndex(0);
    }, 1300); // slightly longer than the 1.2s CSS transition
    return () => clearTimeout(t);
  }, [index, slides.length]);

  // Page Visibility API: stop the timer when tab is hidden so index cannot
  // drift; reset cleanly when the user returns.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        stopTimer.current();
      } else {
        resetSlider.current();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  if (!slides.length) return null;

  const extendedSlides = slides.length > 1 ? [...slides, slides[0]] : slides;
  const activeDot = index % slides.length;

  const goToSlide = (i) => {
    stopTimer.current();
    setAnimated(true);
    setIndex(i);
    // Resume auto-advance after the user taps a dot
    setTimeout(() => startTimer.current(), 4000);
  };

  return (
    <div style={{ overflow: "hidden", borderRadius: 10, position: "relative" }}>
      <div
        style={{
          display: "flex",
          transform: `translateX(-${index * 100}%)`,
          transition: animated ? "transform 1.2s ease-in-out" : "none",
        }}
      >
        {extendedSlides.map((slide, i) => {
          const href = normalizeUrl(slide.link);
          const img = (
            <img
              src={slide.image}
              alt={`Slide ${i + 1}`}
              className="customer-banner-image"
            />
          );
          return (
            <div key={i} style={{ flex: "0 0 100%" }}>
              {href ? (
                <a href={href} target="_blank" rel="noreferrer"
                   className="customer-banner-link">
                  {img}
                </a>
              ) : img}
            </div>
          );
        })}
      </div>

      {slides.length > 1 && (
        <div className="banner-slider-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`banner-slider-dot${i === activeDot ? " active" : ""}`}
              onClick={() => goToSlide(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerSlider;
